import type { ReactNode } from 'react'
import type { Bloco, Celula, Pedaco } from '../api/ajuda'
import { resolverRota } from '../paginas/resolverRota'
import './verbete.css'

/**
 * Desenha o conteúdo de um verbete.
 *
 * 🔴 **Não existe `dangerouslySetInnerHTML` aqui, e isso não é estilo — é a
 * segurança da demanda 040.** O back devolve uma árvore fechada de blocos e
 * pedaços; este componente decide, para cada nó, qual elemento criar. O que não
 * está previsto vira **texto**, e o React o escapa sozinho: `<script>` escrito
 * num verbete aparece na tela como `<script>`, escrito.
 *
 * As marcas são fechadas e cada uma resolve num **token semântico** (`UI-05`).
 * Não há cor escolhida pelo autor: um `#hex` que ninguém validou funciona num
 * tema e some no outro.
 */
export function Verbete({ blocos, marca }: { blocos: Bloco[]; marca?: string }) {
  return (
    <div className="verbete">
      {blocos.map((bloco, i) => (
        <Desenhar key={i} bloco={bloco} marca={marca} />
      ))}
    </div>
  )
}

function Desenhar({ bloco, marca }: { bloco: Bloco; marca?: string }) {
  if (bloco.tipo === 'titulo')
    return (
      <h3 className="verbete__titulo">
        <Pedacos pedacos={bloco.texto} marca={marca} />
      </h3>
    )

  if (bloco.tipo === 'lista')
    return (
      <ul className="verbete__lista">
        {bloco.linhas.map((linha, i) => (
          <li key={i}>
            <Pedacos pedacos={linha.celulas[0]?.pedacos ?? []} marca={marca} />
          </li>
        ))}
      </ul>
    )

  if (bloco.tipo === 'tabela') {
    const [cabecalho, ...corpo] = bloco.linhas

    return (
      // A rolagem horizontal é do contêiner e ele é focável: tabela larga que só
      // rola com o mouse é inacessível pelo teclado (WCAG 2.1.1) — foi defeito
      // real em dez telas, corrigido na 014.
      <div className="verbete__rolagem" tabIndex={0} role="group">
        <table className="verbete__tabela">
          {cabecalho && (
            <thead>
              <tr>
                {cabecalho.celulas.map((celula, i) => (
                  <th key={i} scope="col">
                    <Pedacos pedacos={celula.pedacos} marca={marca} />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {corpo.map((linha, i) => (
              <tr key={i}>
                {linha.celulas.map((celula: Celula, j) => (
                  <td key={j}>
                    <Pedacos pedacos={celula.pedacos} marca={marca} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <p className="verbete__paragrafo">
      <Pedacos pedacos={bloco.texto} marca={marca} />
    </p>
  )
}

function Pedacos({ pedacos, marca }: { pedacos: Pedaco[]; marca?: string }) {
  return (
    <>
      {pedacos.map((pedaco, i) => (
        <Um key={i} pedaco={pedaco} marca={marca} />
      ))}
    </>
  )
}

function Um({ pedaco, marca }: { pedaco: Pedaco; marca?: string }): ReactNode {
  const { texto, destino } = pedaco

  switch (pedaco.marca) {
    case 'negrito':
      return <strong>{texto}</strong>

    case 'codigo':
      return <code className="verbete__codigo">{texto}</code>

    case 'destaque':
    case 'atencao':
    case 'perigo':
    case 'sucesso':
      return <mark className={`verbete__marca verbete__marca--${pedaco.marca}`}>{texto}</mark>

    case 'linkInterno':
      return <a href={resolverRota(destino ?? '', marca)}>{texto}</a>

    case 'linkExterno':
      // Outra origem: nova aba, e `noopener noreferrer` para a aba filha não
      // receber referência à nossa janela nem o cabeçalho de origem.
      return (
        <a href={destino ?? ''} target="_blank" rel="noopener noreferrer">
          {texto}
        </a>
      )

    // Inclui `texto` e qualquer marca que o back venha a devolver e esta versão
    // não conheça. Texto é o padrão seguro: some a formatação, fica o conteúdo.
    default:
      return texto
  }
}

